import { CurrencyPipe, registerLocaleData } from '@angular/common';
import { provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import localeFr from '@angular/common/locales/fr';
import localePt from '@angular/common/locales/pt';
import { ErrorHandler, LOCALE_ID, NgModule, inject, provideAppInitializer } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconRegistry } from '@angular/material/icon';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MAT_RADIO_DEFAULT_OPTIONS, MatRadioDefaultOptions } from '@angular/material/radio';
import { MAT_SELECT_CONFIG, MatSelectConfig } from '@angular/material/select';
import { MAT_SLIDE_TOGGLE_DEFAULT_OPTIONS, MatSlideToggleDefaultOptions } from '@angular/material/slide-toggle';
import { MAT_TABS_CONFIG, MatTabsConfig } from '@angular/material/tabs';
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipDefaultOptions } from '@angular/material/tooltip';
import { BrowserModule, DomSanitizer } from '@angular/platform-browser';
import { provideRouter, withRouterConfig } from '@angular/router';
import { FuseModule } from '@fuse/fuse.module';
import { FuseSharedModule } from '@fuse/shared.module';
import { provideTranslateService } from '@ngx-translate/core';
import prothesisIcons from 'assets/icons/prothesis-icons.svg';
import teethIcons from 'assets/icons/teeth-icons.svg';
import { fr } from 'date-fns/locale';
import { AppComponent } from './app.component';
import { CoreModule } from './core/core.module';
import { RouterHistoryService } from './core/services/router-history.service';
import { SettingsService } from './core/services/settings.service';
import { fuseConfig } from './fuse-config';
import { GlobalErrorHandler } from './global-error-handler';
import { MatPaginatorIntlFr } from './i18n/MatPaginatorIntlFr';
import { LayoutModule } from './layout/layout.module';
import { appRoutes } from './route';
import { MAT_DATE_FNS_DATE_FORMATS_FR } from './shared/mat-date-fns/date-fns-date-formats';
import { LaboratoryDateFnsDateAdapter } from './shared/mat-date-fns/laboratory-date-fns-date-adapter';

export const matRadioDefaultOptions: MatRadioDefaultOptions = {
  color: 'primary',
};

export const matSelectConfig: MatSelectConfig = {
  canSelectNullableOptions: true,
  overlayPanelClass: 'custom-combo-panel',
  panelWidth: 'auto',
};

export const matSlideToggleDefaultOptions: MatSlideToggleDefaultOptions = {
  color: 'primary',
};

export const matTabsConfig: MatTabsConfig = {
  animationDuration: '300ms',
  preserveContent: true,
  stretchTabs: false,
};

export const matTooltipDefaultOptions: MatTooltipDefaultOptions = {
  showDelay: 0,
  hideDelay: 0,
  touchGestures: 'auto',
  position: 'below',
  touchendHideDelay: 0,
  disableTooltipInteractivity: true,
};

/**
 * Registers the SVG icon sets as literals BEFORE the app bootstraps.
 *
 * History: the sprites used to be registered via `addSvgIconSet(url)` in the AppComponent
 * constructor — an asynchronous HTTP load. Shell components (logo, user, theme) could request
 * an icon before the fetch resolved, raising `Error retrieving icon :svg_*! Unable to find
 * icon` (bug #8378). The `<link rel="preload" crossorigin>` added in #8165 was ineffective
 * (CORS mode mismatch with MatIconRegistry's same-origin HttpClient request).
 *
 * Awaiting an `HttpClient` fetch from an APP_INITIALIZER removed that race but kept the sprites
 * on the network, and its fail-soft catch turned a server outage into a silent session-long
 * degradation: whenever the host serving `/assets` was saturated, the app booted without a
 * single custom icon and logged one error per rendered `mat-icon` (bug #8610).
 *
 * The sprites are now inlined at build time by the esbuild `text` loader (see the `loader`
 * option in angular.json), so registration is synchronous and cannot fail: no request, no
 * race, no fail-soft path. See ADR-0024.
 */
export function registerSvgIconSets(): void {
  const registry = inject(MatIconRegistry);
  const sanitizer = inject(DomSanitizer);

  registry.addSvgIconSetLiteral(sanitizer.bypassSecurityTrustHtml(prothesisIcons));
  registry.addSvgIconSetLiteral(sanitizer.bypassSecurityTrustHtml(teethIcons));
}

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    // Angular modules
    BrowserModule,
    // Material modules
    MatButtonModule,
    // Fuse modules
    FuseModule.forRoot(fuseConfig),
    FuseSharedModule,
    // App modules
    LayoutModule,
    CoreModule.forRoot(),
  ],
  providers: [
    provideTranslateService({}),
    provideRouter(
      appRoutes,
      withRouterConfig({
        paramsInheritanceStrategy: 'emptyOnly',
        canceledNavigationResolution: 'replace',
        urlUpdateStrategy: 'deferred',
      }),
    ),
    // Loading LOCALE_ID dynamically: https://stackoverflow.com/questions/44287827/dynamically-change-locale-for-datepipe-in-angular-2
    {
      provide: LOCALE_ID,
      deps: [SettingsService],
      useFactory: (settingsService) => settingsService.getLanguage(),
    },
    { provide: MAT_DATE_LOCALE, useValue: fr },
    { provide: DateAdapter, useClass: LaboratoryDateFnsDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: MAT_DATE_FNS_DATE_FORMATS_FR },
    { provide: MAT_RADIO_DEFAULT_OPTIONS, useValue: matRadioDefaultOptions },
    { provide: MAT_SELECT_CONFIG, useValue: matSelectConfig },
    { provide: MAT_SLIDE_TOGGLE_DEFAULT_OPTIONS, useValue: matSlideToggleDefaultOptions },
    { provide: MAT_TABS_CONFIG, useValue: matTabsConfig },
    { provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: matTooltipDefaultOptions },
    { provide: MatPaginatorIntl, useClass: MatPaginatorIntlFr },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    CurrencyPipe,
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    provideAppInitializer(registerSvgIconSets),
  ],
})
export class AppModule {
  public constructor() {
    const previousRouterService = inject(RouterHistoryService);

    previousRouterService.initialize();
    registerLocaleData(localeFr, 'fr');
    registerLocaleData(localePt, 'pt');
  }
}
